/**
 * The arithmetic of the deck that learns (state/taste.ts), kept free of the app's state so it
 * can be tested on its own.
 */
import type { CoreData } from "../data/types";

export interface Pick {
  score: number;
  /** the liked name that points to it most strongly */
  because: string;
  best: number;
}

/**
 * Count a liked name's neighbours (most alike first). `extra` is the weight not counted before:
 * 1 for a like, 2 for a love, 1 more when a like becomes a love.
 */
export function addNeighbours(picks: Map<string, Pick>, key: string, like: string[], extra: number): void {
  like.forEach((k, j) => {
    // the closest neighbours count most
    const w = extra * (1 - j / 16);
    const p = picks.get(k) ?? { score: 0, because: key, best: 0 };
    p.score += w;
    if (w > p.best) {
      p.best = w;
      p.because = key;
    }
    picks.set(k, p);
  });
}

/** a pick needs a strong enough reason: one like's closest names, or names several likes share */
export const MIN_SCORE = 0.6;

/**
 * The name to bring forward, and the deck's new order with it dealt as the fourth card from now
 * (behind the cards already on the screen). Null when there's nothing worth moving.
 * `order` holds entry indices in the order they'll be dealt; `front` the keys dealt before them.
 */
export function weavePick(
  core: CoreData,
  order: number[],
  front: string[],
  decided: (key: string) => boolean,
  picks: Map<string, Pick>,
): { key: string; because: string; order: number[] } | null {
  const queued = new Set(front);
  // the first three undecided cards in the order are the ones on (or about to be on) the screen
  const soon = new Set<string>(front.filter((k) => !decided(k)));
  let at = -1;
  for (let i = 0, n = 0; i < order.length; i++) {
    const key = core.entries[order[i]].key;
    if (decided(key) || queued.has(key)) continue;
    soon.add(key);
    if (++n === 3) {
      at = i + 1;
      break;
    }
  }
  if (at < 0) return null;
  const ranked = [...picks.entries()]
    .filter(([k, p]) => p.score >= MIN_SCORE && !decided(k) && !soon.has(k))
    .sort((a, b) => b[1].score - a[1].score || (a[0] < b[0] ? -1 : 1));
  if (!ranked.length) return null;
  const pos = new Map<number, number>();
  order.forEach((idx, i) => pos.set(idx, i));
  for (const [k, p] of ranked.slice(0, 40)) {
    const e = core.byKey.get(k);
    const from = e ? pos.get(e.idx) : undefined;
    if (!e || from === undefined || from < at) continue; // not in this deck (filtered out)
    const next = order.slice();
    next.splice(from, 1);
    next.splice(at, 0, e.idx);
    return { key: k, because: p.because, order: next };
  }
  return null;
}
